import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/Home.css';
import '../styles/_colors.css';
import '../styles/_typography.css';
import '../styles/main.css';

export default function Home() {
  const contentRef = useRef(null);
  const statsRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const refs = [contentRef, statsRef, ctaRef];
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div className="home-container">
      <Helmet>
        <title>NHHS Analytics | Home</title>
      </Helmet>
      <section className="hero-banner" style={{backgroundImage: `url(${require('../assets/Bergen.jpg')})`}}>
        <div className="hero-overlay"></div>
        <div className="hero-text">
          <h1>NHHS Analytics</h1>
          <p>
            NHHS Analytics is a student-driven group at NHH for those interested in analytics and data-driven decision making. We welcome both students and business partners to join our journey.
          </p>
          <div className="hero-buttons">
            <a href="/join" className="home-btn">
              Join Now
            </a>
            <a href="/contact" className="partner-btn">
              Partner with Us
            </a>
          </div>
        </div>
      </section>

      <section className="content-section" ref={contentRef}>
        <h2>What We Offer</h2>
        <p>
          Join us as we build something meaningful together. We're creating opportunities for students to develop 
          practical analytics skills while establishing valuable industry connections.
        </p>
        
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <div className="feature-content">
              <h3>Learn Analytics</h3>
              <p>Master data visualization, statistical analysis, and modern analytics tools through hands-on workshops and projects.</p>
            </div>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🤝</div>
            <div className="feature-content">
              <h3>Collaborate & Network</h3>
              <p>Work with fellow students and connect with industry professionals who share your passion for data-driven insights.</p>
            </div>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🎯</div>
            <div className="feature-content">
              <h3>Build Your Portfolio</h3>
              <p>Gain practical experience with real business challenges and create projects that showcase your analytics skills.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats section - uncomment when you have actual numbers
      <section className="stats-section" ref={statsRef}>
        <div className="stats-container">
          <div className="stat-item">
            <h3>50+</h3>
            <p>Active Members</p>
          </div>
          <div className="stat-item">
            <h3>15+</h3>
            <p>Partner Companies</p>
          </div>
          <div className="stat-item">
            <h3>25+</h3>
            <p>Projects Completed</p>
          </div>
          <div className="stat-item">
            <h3>3</h3>
            <p>Years of Excellence</p>
          </div>
        </div>
      </section>
      */}

      <section className="mission-section" ref={statsRef}>
        <div className="mission-container">
          <h2>Our Mission</h2>
          <p>
            As a newly formed group at NHH, we're building a community where students can explore the exciting world of analytics, 
            learn from each other, and create meaningful connections with industry professionals.
          </p>
          
          <div className="mission-points">
            <div className="mission-item">
              <h3>🚀 Getting Started</h3>
              <p>We're actively recruiting passionate students who want to dive into analytics and data science.</p>
            </div>
            
            <div className="mission-item">
              <h3>📚 Learning Together</h3>
              <p>Building our knowledge base through workshops, projects, and peer learning sessions.</p>
            </div>
            
            <div className="mission-item">
              <h3>🔗 Building Connections</h3>
              <p>Establishing relationships with industry partners and creating opportunities for our members.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section" ref={ctaRef}>
        <div className="cta-content">
          <h2>Be Part of Something New</h2>
          <p>Help us build the premier analytics community at NHH. Your journey starts here.</p>
          <div className="cta-buttons">
            <a href="/join" className="cta-btn primary">Join the Movement</a>
            <a href="/team" className="cta-btn secondary">Learn More</a>
          </div>
        </div>
      </section>
      
    </div>
  );
}

